/**
 * HMAC Generator Tool
 *
 * Generate HMAC using Web Crypto API.
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const HmacGeneratorTool: ITransformerTool = {
  id: 'hmac-generator',
  name: 'HMAC Generator',
  description: 'Generate HMAC using Web Crypto API',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['hmac', 'hash', 'mac', 'authentication', 'sha256', 'crypto', 'signature'],

  seo: {
    slug: 'hmac-generator',
    pageTitle: 'HMAC Generator - Free Online SHA-256/384/512 Tool',
    metaDescription: 'Generate HMAC signatures using SHA-256, SHA-384, or SHA-512 with the Web Crypto API. Supports hex and base64 output. Runs entirely in your browser.',
    keywords: ['hmac generator', 'hmac sha256', 'hmac calculator', 'hmac online', 'message authentication code'],
    relatedTools: ['hash-generator', 'password-generator'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.HASH,

  attributes: [
    {
      name: 'algorithm',
      type: 'select',
      label: 'Algorithm',
      defaultValue: 'SHA-256',
      options: ['SHA-256', 'SHA-384', 'SHA-512'],
      description: 'HMAC algorithm',
    },
    {
      name: 'secretKey',
      type: 'string',
      label: 'Secret Key',
      defaultValue: '',
      description: 'Secret key for HMAC generation',
    },
    {
      name: 'outputFormat',
      type: 'select',
      label: 'Output Format',
      defaultValue: 'hex',
      options: ['hex', 'base64'],
      description: 'Output encoding format',
    },
    {
      name: 'rawOutput',
      type: 'boolean',
      label: 'Raw Output',
      defaultValue: false,
      description: 'Output only the raw hash digest with no labels',
    },
  ],

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const message = String(input ?? '');
    const algorithm = String(config.algorithm ?? 'SHA-256');
    const secretKey = String(config.secretKey ?? '');
    const outputFormat = String(config.outputFormat ?? 'hex');
    const rawOutput = Boolean(config.rawOutput);

    if (!secretKey) throw ToolError.validation('Secret key is required');
    if (!message) throw ToolError.validation('Message input is required');

    const encoder = new TextEncoder();
    const keyData = encoder.encode(secretKey);
    const messageData = encoder.encode(message);

    const cryptoKey = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: algorithm },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign('HMAC', cryptoKey, messageData);
    const bytes = new Uint8Array(signature);

    let result: string;
    if (outputFormat === 'base64') {
      result = btoa(String.fromCharCode(...bytes));
    } else {
      result = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    }

    if (rawOutput) {
      return result;
    }

    return [
      `HMAC (${algorithm}):`,
      result,
      '',
      `Algorithm: ${algorithm}`,
      `Output: ${outputFormat}`,
      `Message length: ${message.length} characters`,
      `Key length: ${secretKey.length} characters`,
    ].join('\n');
  },
};
